import { Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import VoiceMemoPlaybackPopper from "@/Dashboard/containers/MediaDialogs/VoiceMemoPlaybackPopper";
import {
  ELEMENT_COLOR_VALUE,
  ELEMENT_LINK_DESTINATION,
  ELEMENT_SCOPE,
  ELEMENT_SHAPE,
  ELEMENT_STATUS,
  ELEMENT_TEXT_COLOR_VALUE,
  ELEMENT_TYPE,
  ROLES,
  TAB_STATUSES,
} from "@/constants";
import { useUrlParams } from "@/hooks";
import { getCurrentElement, setHotspotData, setSimpleValue } from "@/store/actions";
import { hotspotDataSelector, simpleValuesSelector } from "@/store/selectors";
import { ELEMENT_DEFAULT_ICONS_VALUE, ELEMENT_ICONS_VALUE } from "@/utils/element";
import { getMediaLink } from "@/utils/mediaLink";
import { isPublicPage } from "@/utils/visualBuilder.js";

const useStyles = makeStyles({
  elementLink: {
    width: (props) => (props.elementPositioning === "experience" ? 30 : 60),
    height: (props) => (props.elementPositioning === "experience" ? 30 : 60),
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 8,
    borderWidth: 0,
    marginLeft: (props) => (props.isMobile ? 0 : 10),
    "&:hover": {
      cursor: "pointer",
    },
    "&:first-child": {
      marginLeft: 0,
    },
  },
  popover: {
    pointerEvents: (props) => props.mapOpen && "none",
    marginTop: 10,
  },
  elementText: {
    margin: "0px",
    overflowX: "hidden",
    whiteSpace: "nowrap",
    color: (props) => ELEMENT_TEXT_COLOR_VALUE[props.textColor] || "#fff",
  },
  icon: {
    display: "block",
  },
});

export default function Element(props) {
  const {
    elementPositioning,
    newRef,
    elementData,
    onPopoverToggle,
    isOpen,
    anchor,
    forcePopoverOpen,
    tabStatus,
    noLabels,
    isMobile,
    locked,
    mdDown,
  } = props;

  const params = useUrlParams();
  const { propertyId, role } = params;

  const {
    showText = false,
    textColor,
    color: elementColor,
    size,
    shape,
    type: elementType,
    scope: elementScope,
  } = elementData;

  const isSquare = shape === ELEMENT_SHAPE.SQUARE || false;
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const { "public-page": publicPage = false, "voice-memo-playback": voiceMemoPlayback } =
    useSelector(simpleValuesSelector);

  const { mapHotspotCanvas = {} } = useSelector(hotspotDataSelector(propertyId));

  const { open: mapOpen } = mapHotspotCanvas;

  const classes = useStyles({
    elementPositioning,
    showText,
    isSquare,
    elementColor,
    textColor,
    mdDown,
    noLabels,
    size,
    mapOpen,
    type: elementType,
    isMobile,
  });

  const isContent = elementType === ELEMENT_TYPE.CONTENT || elementScope === ELEMENT_SCOPE.CONTENT;

  const isContentPath = isContent || tabStatus === TAB_STATUSES.HOMEBASE || false;

  let disabled =
    isContent && [TAB_STATUSES.COLLECT, TAB_STATUSES.CUSTOMIZE].includes(tabStatus) && !isPublicPage(tabStatus);

  if (isContent && role === ROLES.SALESPERSON && tabStatus === TAB_STATUSES.HOMEBASE) {
    disabled = true;
  }

  if (locked && !isPublicPage(tabStatus)) {
    disabled = true;
  }

  const handleOpacity = () => {
    if (
      isContent &&
      tabStatus === TAB_STATUSES.HOMEBASE &&
      elementData.status === ELEMENT_STATUS.CREATED &&
      role !== ROLES.SALESPERSON
    ) {
      return 1;
    }
    if (elementData.status === ELEMENT_STATUS.DRAFT || disabled) {
      return 0.5;
    }
    return 1;
  };

  const elementRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isOpen && anchor !== buttonRef.current) {
      forcePopoverOpen(buttonRef.current);
    }
  }, []);

  const togglePopover = () => {
    if (
      isContent &&
      ([TAB_STATUSES.COLLECT, TAB_STATUSES.CUSTOMIZE].includes(tabStatus) ||
        (role === ROLES.SALESPERSON && tabStatus === TAB_STATUSES.HOMEBASE))
    ) {
      return;
    }

    if (locked && !isPublicPage(tabStatus)) {
      return;
    }

    dispatch(
      getCurrentElement({
        propertyId,
        experienceId: elementData.experienceId,
        mediaItemId: elementData.mediaItemId,
        elementId: elementData.id,
      }),
    );

    if ((!publicPage && !isContentPath) || (isContentPath && tabStatus === TAB_STATUSES.HOMEBASE)) {
      if (elementType === ELEMENT_TYPE.MAP) {
        dispatch(
          setHotspotData(propertyId, {
            mapHotspotCanvas: {
              elementId: elementData.id,
              url: elementData.url,
              open: !mapOpen,
            },
          }),
        );
      }
      onPopoverToggle(elementData.id);
      setOpen(!open);
    } else if (elementType === ELEMENT_TYPE.VOICE_MEMO && publicPage) {
      const sameElement = voiceMemoPlayback?.element?.id === elementData.id;
      const open = sameElement ? !voiceMemoPlayback?.open : true;
      dispatch(
        setSimpleValue("voice-memo-playback", {
          open,
          anchor: buttonRef.current,
          element: elementData,
        }),
      );
    } else if (elementData.linkDestination === ELEMENT_LINK_DESTINATION.NEW_TAB) {
      window.open(elementData.url, "_blank");
    } else if (elementType === ELEMENT_TYPE.MAP) {
      dispatch(
        setHotspotData(propertyId, {
          mapHotspotCanvas: {
            elementId: elementData.id,
            url: elementData.url,
            open: !mapOpen,
          },
        }),
      );
    } else {
      dispatch(
        setSimpleValue("elementLinkDialog", {
          open: true,
        }),
      );
    }
  };

  const iconProps = {
    htmlColor: ELEMENT_TEXT_COLOR_VALUE[elementData.textColor] || "#fff",
  };

  const IconComponent = ELEMENT_ICONS_VALUE[elementType] || ELEMENT_ICONS_VALUE[ELEMENT_TYPE.LINK];

  const getIconPath = (icon, elementData) =>
    ELEMENT_DEFAULT_ICONS_VALUE.includes(icon.link)
      ? `/icons/v2/image_${elementData.textColor}/${icon.name}.svg`
      : getMediaLink({ link: icon.link });

  const renderTypeIcon = (icon = {}) => {
    let title = elementData?.title || "";

    if (title.length > 20) {
      title = `${title.slice(0, 20)}...`;
    }

    const elementTextStyles = {
      width: "100%",
      fontSize: "14px",
    };

    const elementIconStyles = {
      maxWidth: "28px",
      maxHeight: "28px",
    };

    return (
      <>
        {icon.link ? (
          <img src={getIconPath(icon, elementData)} alt="" className={classes.icon} style={elementIconStyles} />
        ) : (
          <span ref={elementRef} style={{ lineHeight: "0", display: "inline-block" }}>
            <IconComponent className={classes.icon} style={elementIconStyles} {...iconProps} />
          </span>
        )}
        {!mdDown && !noLabels && elementData?.showText && elementData?.title && (
          <span className={classes.elementText} style={elementTextStyles}>
            {title}
          </span>
        )}
      </>
    );
  };

  const elementLinkStyles = {
    height: 40,
    borderRadius: !mdDown && showText ? "30px" : "50%",
    padding: !mdDown && showText ? "6px 18px" : "10px",
  };

  if (!elementData?.title || !elementData?.showText || mdDown) {
    elementLinkStyles.minWidth = 40;
    elementLinkStyles.width = 40;
  }

  const content = (
    <>
      <button
        type="button"
        ref={buttonRef}
        key={elementData.id}
        onClick={() => togglePopover()}
        className={classes.elementLink}
        style={{
          width: "100%",
          ...elementLinkStyles,
          backgroundColor: ELEMENT_COLOR_VALUE[elementColor] || ELEMENT_COLOR_VALUE.DEFAULT,
          opacity: handleOpacity(),
          cursor: disabled && "not-allowed",
        }}
        tabIndex={0}
        disabled={disabled}
        data-testid="element-container"
      >
        {renderTypeIcon({
          link: elementData.iconLink,
          name: elementData.iconName,
        })}
      </button>
      {isPublicPage && elementType === ELEMENT_TYPE.VOICE_MEMO && isOpen && <VoiceMemoPlaybackPopper />}
    </>
  );

  return isMobile ? (
    content
  ) : (
    <Tooltip
      classes={{
        arrow: classes.arrow,
        tooltip: classes.tooltip,
      }}
      title={disabled ? elementData?.title : ""}
      placement="bottom"
      arrow
      ref={newRef}
    >
      {content}
    </Tooltip>
  );
}
